import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../../lib/api";

interface WikiListItem {
  id: string;
  title: string;
  tags: string[];
  author_name: string | null;
  created_at: string;
  updated_at: string;
}

export const Route = createFileRoute("/_authed/wiki/")({
  component: WikiListPage,
});

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

function WikiListPage() {
  const [q, setQ] = useState("");
  const [tag, setTag] = useState<string | null>(null);

  const { data: articles = [], isLoading } = useQuery<WikiListItem[]>({
    queryKey: ["wiki-articles", q, tag],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (tag) params.set("tag", tag);
      const qs = params.toString();
      return api.get(`/wiki${qs ? `?${qs}` : ""}`);
    },
  });

  const { data: tags = [] } = useQuery<string[]>({
    queryKey: ["wiki-tags"],
    queryFn: () => api.get("/wiki/tags"),
  });

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Wiki</h1>
          <Link
            to="/wiki/new"
            style={{ padding: "8px 16px", background: "var(--brand)", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
          >
            + Novo artigo
          </Link>
        </div>

        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Buscar por título ou conteúdo..."
          style={{
            padding: "10px 14px", background: "var(--surface-input)", border: "1px solid var(--line)",
            borderRadius: 8, color: "var(--ink)", fontSize: 14, outline: "none", width: "100%", boxSizing: "border-box",
          }}
        />

        {tags.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {tags.map(t => (
              <button
                key={t}
                onClick={() => setTag(tag === t ? null : t)}
                style={{
                  fontSize: 12, padding: "3px 12px", borderRadius: 99, cursor: "pointer",
                  background: tag === t ? "var(--brand-soft)" : "var(--surface-hover)",
                  color: tag === t ? "var(--brand)" : "var(--ink-secondary)",
                  border: tag === t ? "1px solid var(--brand)" : "1px solid var(--line)",
                }}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {isLoading && <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando...</span>}

        {!isLoading && articles.length === 0 && (
          <div style={{ textAlign: "center", padding: "48px 0", color: "var(--ink-muted)", fontSize: 14 }}>
            {q || tag ? "Nenhum artigo encontrado para essa busca." : "Nenhum artigo ainda. Escreva o primeiro!"}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {articles.map(a => (
            <Link
              key={a.id}
              to="/wiki/$articleId"
              params={{ articleId: a.id }}
              style={{
                display: "block", padding: "14px 18px", background: "var(--surface-hover)",
                border: "1px solid var(--line)", borderRadius: 10, textDecoration: "none", color: "inherit",
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{a.title}</span>
                <span style={{ fontSize: 12, color: "var(--ink-dim)", flexShrink: 0 }}>{fmtDate(a.updated_at)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                {a.tags.map(t => (
                  <span key={t} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--brand-soft)", color: "var(--brand)" }}>
                    {t}
                  </span>
                ))}
                <span style={{ fontSize: 12, color: "var(--ink-muted)", marginLeft: "auto" }}>
                  {a.author_name ?? "—"}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
